/**
 * useTheme — shared reactive state for the light/dark theme switch (ADR-074).
 *
 * `theme` is a module-level ref (singleton, same pattern as useAuth),
 * initialised from the `data-theme` attribute the pre-paint script in
 * `index.html` already set. With no stored choice, the theme follows
 * `prefers-color-scheme` live; an explicit `setTheme` (via `toggleTheme`)
 * stores the choice in localStorage and the media-query listener stops
 * applying further OS changes.
 */
import { ref } from 'vue'

type Theme = 'light' | 'dark'

const STORAGE_KEY = 'ordiria:theme'

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
}

const theme = ref<Theme>(readInitialTheme())

function applyTheme(t: Theme) {
  if (typeof document !== 'undefined') document.documentElement.dataset.theme = t
  theme.value = t
}

function setTheme(t: Theme) {
  applyTheme(t)
  try {
    localStorage.setItem(STORAGE_KEY, t)
  } catch {
    // storage unavailable (private mode, quota) — the attribute change still applies
  }
}

function toggleTheme() {
  setTheme(theme.value === 'dark' ? 'light' : 'dark')
}

// With no stored choice, follow prefers-color-scheme changes live. Stops
// applying as soon as an explicit choice has been stored.
if (typeof window !== 'undefined' && typeof matchMedia === 'function') {
  const media = matchMedia('(prefers-color-scheme: dark)')
  media.addEventListener('change', (e) => {
    let stored: string | null = null
    try {
      stored = localStorage.getItem(STORAGE_KEY)
    } catch {
      // storage unavailable — treat as "no stored choice"
    }
    if (stored !== 'dark' && stored !== 'light') applyTheme(e.matches ? 'dark' : 'light')
  })
}

export function useTheme() {
  return { theme, setTheme, toggleTheme }
}
